import { type FC, type ReactNode, useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { useCustomStyles } from './Countdown.styles';

export interface CountdownProps {
	expirationDate: string | Date;
	delimiter?: string;
	expiredContent?: ReactNode | string;
}

const pad = (value: number): string => ((value + '').length >= 2 ? value + '' : ('00' + value).slice(-2));

/**
 * Unstyled lightweight Countdown component
 * with no dependencies on react-countdown
 *
 * @param expirationDate Expiration date
 * @param delimiter			 Delimiter
 * @param expiredContent Content to be rendered when date is reached
 * @constructor
 */
export const Countdown: FC<CountdownProps> = ({ expirationDate, delimiter = ' : ', expiredContent = null }) => {
	const { classes, cx } = useCustomStyles();
	const [countdownText, setCountdownText] = useState<string | null>(' ');

	useEffect(() => {
		const intervalId = setInterval(() => {
			const totalSeconds = dayjs(expirationDate).diff(dayjs(), 'seconds');
			if (totalSeconds > 0) {
				const seconds = totalSeconds % 60;
				const minutes = Math.trunc(totalSeconds / 60) % 60;
				const hours = Math.trunc(totalSeconds / 3600) % 24;
				const days = Math.trunc(totalSeconds / (3600 * 24));
				setCountdownText(
					`${pad(days)}${delimiter}${pad(hours)}${delimiter}${pad(minutes)}${delimiter}${pad(seconds)}`,
				);
			} else {
				setCountdownText(null);
			}
		}, 1000);
		return () => clearInterval(intervalId);
	}, [expirationDate, delimiter, setCountdownText]);

	return <span className={cx(countdownText && classes.numericFont)}>{countdownText || expiredContent}</span>;
};
